import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import polyline from "@mapbox/polyline"

export default class extends Controller {
  static targets = ["canvas", "empty"]
  static values = { data: Object }

  connect() {
    const points = this.mapPoints()
    if (points.length === 0) {
      this.showEmptyState()
      return
    }

    this.map = L.map(this.canvasTarget, { zoomControl: true })
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=cb1_25p6_1_34524d380d02f77504dcc6c5', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd', maxZoom: 20
    }).addTo(this.map);

    this.addRouteLine()
    this.addOriginMarker()
    this.addStopMarkers()
    this.addTrackingMarker()
    this.map.fitBounds(L.latLngBounds(points), { padding: [36, 36], maxZoom: 16 })
  }

  disconnect() {
    this.map?.remove()
  }

  addRouteLine() {
    const coordinates = this.routeCoordinates()
    if (coordinates.length < 2) return

    L.polyline(coordinates, {
      color: "#0d6efd",
      opacity: 0.9,
      weight: 5,
      lineCap: "round",
      lineJoin: "round"
    }).addTo(this.map)
  }

  addOriginMarker() {
    const origin = this.validPoint(this.dataValue.origin)
    if (!origin) return

    const marker = L.marker(origin, {
      icon: this.markerIcon("admin-route-map__origin-marker", '<div class="admin-route-map__origin-pin"><i class="bi bi-building"></i></div>')
    }).addTo(this.map)
    marker.bindPopup(this.popupContent(this.dataValue.origin?.label || "Armazém"))
  }

  addStopMarkers() {
    this.stops().forEach((stop) => {
      const coordinates = this.validPoint(stop)
      if (!coordinates) return

      const statusClass = stop.status === "COMPLETED"
        ? " admin-route-map__stop-pin--completed"
        : (stop.status === "SKIPPED" ? " admin-route-map__stop-pin--skipped" : "")
      const marker = L.marker(coordinates, {
        icon: this.markerIcon(
          "admin-route-map__stop-marker",
          `<div class="admin-route-map__stop-pin${statusClass}">${stop.sequence || ""}</div>`
        )
      }).addTo(this.map)
      marker.bindPopup(this.stopPopupContent(stop))
    })
  }

  addTrackingMarker() {
    const tracking = this.validPoint(this.dataValue.last_tracking)
    if (!tracking) return

    const marker = L.marker(tracking, {
      icon: this.markerIcon("admin-route-map__tracking-marker", '<div class="admin-route-map__tracking-pin"><i class="bi bi-navigation-fill"></i></div>')
    }).addTo(this.map)
    marker.bindPopup(this.popupContent(this.dataValue.last_tracking?.label || "Última posição"))
  }

  routeCoordinates() {
    const encoded = this.dataValue.encoded_polyline
    if (encoded) {
      try {
        const decoded = polyline.decode(encoded, 6).filter((point) => this.validPoint({ latitude: point[0], longitude: point[1] }))
        if (decoded.length >= 2) return decoded
      } catch (_error) {
        // Fall back to a straight line through the known route points.
      }
    }

    return [this.dataValue.origin, ...this.stops()]
      .map((point) => this.validPoint(point))
      .filter(Boolean)
  }

  mapPoints() {
    return [this.dataValue.origin, ...this.stops(), this.dataValue.last_tracking]
      .map((point) => this.validPoint(point))
      .filter(Boolean)
  }

  stops() {
    return Array.isArray(this.dataValue.stops) ? this.dataValue.stops : []
  }

  validPoint(point) {
    const latitude = Number(point?.latitude)
    const longitude = Number(point?.longitude)
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null

    return [latitude, longitude]
  }

  markerIcon(className, html) {
    return L.divIcon({ className, html, iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -16] })
  }

  popupContent(label) {
    const container = document.createElement("div")
    container.className = "fw-semibold"
    container.textContent = label
    return container
  }

  stopPopupContent(stop) {
    const container = document.createElement("div")
    const title = document.createElement("div")
    const details = document.createElement("div")
    title.className = "fw-semibold"
    title.textContent = `Parada ${stop.sequence || "-"}`
    details.className = "small text-muted"
    const count = Number(stop.orders_count) || 0
    details.textContent = `${count} ${count === 1 ? "pedido" : "pedidos"}`
    container.append(title, details)
    return container
  }

  showEmptyState() {
    this.canvasTarget.classList.add("d-none")
    this.emptyTarget.classList.remove("d-none")
  }
}
